import type { ColumnState } from 'ag-grid-community';
import GAgGrid, { type GAgGridProps } from './GAgGrid.tsx';
import { debounce } from 'lodash/fp';
import { type ReactElement, useRef } from 'react';
import { useAuth } from 'components/management/NewAuthProvider';
import type { UserSettings } from 'components/management/UserSettings.types.ts';
import { useDefaultErrorHandling } from 'components/technical/UseDefaultErrorHandling.tsx';
import {
  type IUpdateUserSettingsMutationFn,
  useUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from 'generated/graphql.tsx';
import { validateColDefs } from './validateColId.ts';
import type { AgGridReact } from 'ag-grid-react';

type GAgGridPreserveStateProps<RowData> = GAgGridProps<RowData> & {
  userSettingsKey: UserSettings;
};

function updateReportSettings(
  updateSettings: IUpdateUserSettingsMutationFn,
  userSettingsKey: UserSettings,
  settings: ColumnState[]
): void {
  updateSettings({
    variables: {
      settings: { [userSettingsKey]: settings },
    },
    ignoreResults: true,
  });
}

const DEBOUNCE_TIMEOUT_MS = 3000;
// outside of component to proper debounce
const debouncedUpdateReportSettings = debounce(DEBOUNCE_TIMEOUT_MS, updateReportSettings);

/** wrapper around GAgGrid, saves column state using UpdateUserSettingsMutation */
function GAgGridPreserveState<RowData>(props: GAgGridPreserveStateProps<RowData>): ReactElement {
  const localRef = useRef<AgGridReact<RowData> | null>(null);
  const [updateSettings] = useUpdateUserSettingsMutation();

  const userSettingsQueryResult = useDefaultErrorHandling(
    useUserSettingsQuery({
      variables: { field: props.userSettingsKey },
    }),
    { disableNoDataFallback: true }
  );

  const { isImpersonating } = useAuth();

  if (!userSettingsQueryResult.loaded) {
    return <userSettingsQueryResult.Fallback />;
  }

  const savedColumnState = userSettingsQueryResult.data.management.userSettings;

  return (
    <GAgGrid<RowData>
      {...props}
      ref={(instance) => {
        localRef.current = instance;
        if (typeof props.ref === 'function') props.ref(instance);
        else if (props.ref) props.ref.current = instance;
      }}
      onDisplayedColumnsChanged={(event): void => {
        const currentColIds = event.api
          .getAllGridColumns()
          .map((c) => c.getColId())
          .filter(Boolean);

        validateColDefs(
          event.api.getAllGridColumns().map((c) => c.getColDef()),
          currentColIds
        );

        if (!isImpersonating) {
          debouncedUpdateReportSettings(updateSettings, props.userSettingsKey, event.api.getColumnState());
        }

        props.onDisplayedColumnsChanged?.(event);
      }}
      onGridReady={(agGrid): void => {
        const currentColIds = agGrid.api
          .getAllGridColumns()
          .map((c) => c.getColId())
          .filter(Boolean);

        validateColDefs(
          agGrid.api.getAllGridColumns().map((c) => c.getColDef()),
          currentColIds
        );

        if (savedColumnState) {
          const state = savedColumnState.map((column: ColumnState) => ({
            ...column,
            width: undefined, // allow auto-size
          }));

          agGrid.api.applyColumnState({
            state,
            applyOrder: false,
          });

          setTimeout(() => {
            agGrid.api.autoSizeAllColumns();
          }, 1);
        }

        props.onGridReady?.(agGrid);
      }}
    />
  );
}

export default GAgGridPreserveState;
